import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Card, Typography, message, Space } from 'antd';
import { UserOutlined, LockOutlined, LoginOutlined, SafetyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { adminAuthApi } from '@/services/api';

const { Title, Text } = Typography;

interface LoginFormData {
  username: string;
  password: string;
  verificationCode?: string;
}

const isValidJwt = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1] || ''));
    const expSec = Number(payload?.exp || 0);
    const nowSec = Math.floor(Date.now() / 1000);
    return !!expSec && expSec > nowSec;
  } catch {
    return false;
  }
};

const AdminLogin: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [loginData, setLoginData] = useState<{ username: string; password: string } | null>(null);
  const [codeIssuedAt, setCodeIssuedAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const [hasShownExpiryMsg, setHasShownExpiryMsg] = useState(false);
  const navigate = useNavigate();

  // 이미 유효한 관리자 토큰이 있으면 로그인 페이지 접근 시 대시보드로 이동
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token && isValidJwt(token)) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (!codeIssuedAt) return;
    setNow(Date.now());
    setHasShownExpiryMsg(false);

    const timerId = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(timerId);
  }, [codeIssuedAt]);

  const elapsedMs = codeIssuedAt ? now - codeIssuedAt : 0;
  const resendEnabled = codeIssuedAt ? elapsedMs >= 60_000 : false;
  const resendCountdown = codeIssuedAt ? Math.max(0, Math.ceil((60_000 - elapsedMs) / 1000)) : 60;
  const codeExpired = codeIssuedAt ? elapsedMs >= 180_000 : false;

  useEffect(() => {
    if (codeExpired && !hasShownExpiryMsg) {
      message.warning('인증번호가 만료되었습니다. 다시 요청해주세요');
      setHasShownExpiryMsg(true);
    }
  }, [codeExpired, hasShownExpiryMsg]);

  const resetVerificationState = () => {
    setCodeIssuedAt(null);
    setHasShownExpiryMsg(false);
  };

  const onFinish = async (values: LoginFormData) => {
    setLoading(true);
    try {
      if (currentStep === 0) {
        await adminAuthApi.login({ loginId: values.username, password: values.password });
        message.success('인증번호를 이메일로 발송했습니다. 3분 내 입력하세요.');
        setLoginData({ username: values.username, password: values.password });
        setCurrentStep(1);
        setCodeIssuedAt(Date.now());
      } else if (currentStep === 1) {
        if (codeExpired) {
          message.warning('인증번호가 만료되었습니다. 다시 요청해주세요');
          return;
        }
        const res = await adminAuthApi.verify({ loginId: loginData?.username || '', code: values.verificationCode || '' });
        const token = res?.data?.token || res?.token; // ApiResponse 래핑 대응
        const refreshToken = res?.data?.refreshToken || res?.refreshToken;
        if (token) localStorage.setItem('admin_token', token);
        if (refreshToken) localStorage.setItem('admin_refresh_token', refreshToken);
        message.success('로그인 성공!');
        navigate('/admin/dashboard');
        resetVerificationState();
      }
    } catch (error: any) {
      const msg = error?.response?.data?.message || '로그인 중 오류가 발생했습니다.';
      message.error(msg);
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!loginData || !resendEnabled) return;
    setLoading(true);
    try {
      await adminAuthApi.login({ loginId: loginData.username, password: loginData.password });
      message.success('인증번호를 다시 발송했습니다. 3분 내 입력하세요.');
      setCodeIssuedAt(Date.now());
    } catch (error: any) {
      const msg = error?.response?.data?.message || '인증번호 재전송 중 오류가 발생했습니다.';
      message.error(msg);
      console.error('Resend code error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <Card
        style={{
          width: '100%',
          maxWidth: '400px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          borderRadius: '12px'
        }}
        bodyStyle={{ padding: '40px' }}
      >
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Title level={2} style={{ marginBottom: '8px', color: '#1890ff' }}>
            관리자 로그인
          </Title>
          <Text type="secondary">
            {currentStep === 0 ? '관리자 계정으로 로그인하세요' : '인증번호를 입력해주세요'}
          </Text>
        </div>



        <Form
          name="admin_login"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
          size="large"
          initialValues={loginData || {}}
        >
          {currentStep === 0 ? (
            <>
              <Form.Item
                name="username"
                rules={[
                  { required: true, message: '아이디를 입력해주세요!' },
                  { min: 3, message: '아이디는 최소 3자 이상이어야 합니다!' }
                ]}
              >
                <Input
                  prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                  placeholder="아이디"
                  style={{ borderRadius: '8px' }}
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[
                  { required: true, message: '비밀번호를 입력해주세요!' },
                  { min: 6, message: '비밀번호는 최소 6자 이상이어야 합니다!' }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                  placeholder="비밀번호"
                  style={{ borderRadius: '8px' }}
                />
              </Form.Item>
            </>
          ) : (
            <Form.Item
              name="verificationCode"
              rules={[
                { required: true, message: '인증번호를 입력해주세요!' },
                { len: 6, message: '인증번호는 6자리입니다!' }
              ]}
            >
              <Input
                prefix={<SafetyOutlined style={{ color: '#bfbfbf' }} />}
                placeholder="인증번호 6자리"
                style={{ borderRadius: '8px' }}
                maxLength={6}
              />
            </Form.Item>
          )}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={currentStep === 0 ? <LoginOutlined /> : <SafetyOutlined />}
              style={{
                width: '100%',
                height: '48px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              {currentStep === 0 ? '로그인' : '인증하기'}
            </Button>
          </Form.Item>

          {currentStep === 1 && (
            <Form.Item>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  onClick={handleResendCode}
                  disabled={!resendEnabled || loading || !loginData}
                  style={{ width: '100%' }}
                >
                  {resendEnabled ? '인증번호 재전송' : `재전송 대기중... (${resendCountdown}s)`}
                </Button>
                {codeExpired && (
                  <Text type="danger" style={{ fontSize: '12px' }}>
                    인증번호가 만료되었습니다. 다시 요청해주세요
                  </Text>
                )}
              </Space>
            </Form.Item>
          )}

          {currentStep === 1 && (
            <Form.Item>
              <Button
                type="link"
                onClick={() => {
                  setCurrentStep(0);
                  resetVerificationState();
                }}
                style={{ width: '100%' }}
              >
                이전 단계로 돌아가기
              </Button>
            </Form.Item>
          )}
        </Form>

        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <Space direction="vertical" size="small">
            <Text type="secondary" style={{ fontSize: '12px' }}>
              테스트 계정: admin / admin123
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              테스트 인증번호: 123456
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              © 2024 Alpha Eng. All rights reserved.
            </Text>
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default AdminLogin;
